import "./PrincipalComponentAnalysis.css";

import {
  ANALYTICS_EVENTS,
  MATRIX_FILL_METHODS,
  OTHER_NUMERICAL_PROPERTIES_GROUP,
} from "../../../constants";
import { BAR_CHART_TITLE, BarChart } from "./components/BarChart";
import {
  Badge,
  Button,
  Collapse,
  Nav,
  NavItem,
  NavLink,
  PopoverBody,
  TabContent,
  TabPane,
  UncontrolledPopover,
} from "reactstrap";
import { CIRCLE_CHART_TITLE, CircleChart } from "./components/CircleChart";
import { PCAScatter, PCA_SCATTER_TITLE } from "./components/PCAScatter";
import {
  selectFeaturesByDateWithAUCData,
  selectFilteredAggregatedFeatures,
  selectFilteredFeaturesByDateAndExcludedGroups,
  selectMergedTraits,
} from "../../../selectors/resultMap";
import { useEffect, useMemo, useRef, useState } from "react";

import { Chart } from "../../components/charts/Chart";
import { ControlSection } from "../../components/ControlSection/ControlSection";
import { FocusList } from "../../components/focusList/focusList";
import { HELP_TOOLTIPS } from "../../../helpTooltips/helpTooltips";
import { HelpTooltip } from "../../components/HelpTooltip/HelpTooltip";
import { OffCanvasWrapper } from "../../components/advancedFiltering/OffCanvasWrapper";
import { PCASettings } from "./components/PCASettings";
import { PlotHistory } from "./components/PlotHistory";
import PropTypes from "prop-types";
import { TraitsDatesMatrix } from "../../components/TraitsDatesMatrix/TraitsDatesMatrix";
import { TrialPreferences } from "../../../WorldMap/MapLayerControl/components/TrialPreferences/TrialPreferences";
import { performPCA } from "./pca";
import { selectTraitsIndexedOnTechnicalName } from "../../../selectors/traits";
import { useKeyboardShortcut } from "../../../hooks/useKeyboardShortcut";
import { useSelector } from "react-redux";
import { useTracking } from "../../../analytics";

const DEFAULT_PCA_OPTIONS = {
  threshold: 0.05,
  useFilteredData: false,
  autoCompute: true,
  fillMethod: MATRIX_FILL_METHODS.DROP,
};

/* Gear button on a chart card that pops the PC-axis pickers. The axes drive
 * every chart, so the same control is reachable from each card (shared state).
 * A plain button list (not a dropdown) shows every option at once and avoids
 * the menu-clipping issues react-select hits inside a popover.
 * `id` must be unique per instance (it targets the popover). */
const DisplayedComponents = ({ id, options, selected, setSelected }) => (
  <>
    <Button
      id={id}
      size="sm"
      color="light"
      className="pca-gear-btn"
      title="Displayed components"
    >
      <i className="fa-solid fa-gear" />
    </Button>
    <UncontrolledPopover trigger="legacy" placement="bottom-end" target={id}>
      <PopoverBody className="pca-components-popover">
        {[0, 1].map((axis) => (
          <div className="pca-comp-group" key={axis}>
            <span className="pca-comp-label">Component {axis + 1}</span>
            {options.map((option) => (
              <button
                type="button"
                key={option.value}
                className={`pca-comp-opt ${
                  selected[axis] === option.value ? "active" : ""
                }`}
                onClick={() =>
                  setSelected(
                    axis === 0
                      ? [option.value, selected[1]]
                      : [selected[0], option.value]
                  )
                }
              >
                {option.label}
              </button>
            ))}
          </div>
        ))}
      </PopoverBody>
    </UncontrolledPopover>
  </>
);

DisplayedComponents.propTypes = {
  id: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  selected: PropTypes.arrayOf(PropTypes.number).isRequired,
  setSelected: PropTypes.func.isRequired,
};

export const PrincipalComponentAnalysis = ({ drawerOpen, setDrawerOpen }) => {
  const allTraits = useSelector(selectTraitsIndexedOnTechnicalName);
  const mergedTraits = useSelector(selectMergedTraits);
  // True while the date=all features are still paginating in — drives the chart
  // spinners so the empty cards read as "loading" instead of "no data".
  const refreshingFeaturesByDate = useSelector(
    (state) => state.resultMap.refreshingFeaturesByDate
  );

  const [options, setOptions] = useState(DEFAULT_PCA_OPTIONS);
  const [selectedComponents, setSelectedComponents] = useState([0, 1]);
  const [clickedPlots, setClickedPlots] = useState([]);

  const featuresByDate = useSelector(
    options.useFilteredData
      ? selectFilteredFeaturesByDateAndExcludedGroups
      : selectFeaturesByDateWithAUCData
  );

  /* Compute which dates have which traits delivered */
  const traitsWithDates = useMemo(() => {
    // Avoid filling with any data if featuresByDate hasn't loaded yet
    if (featuresByDate.length === 0) return [];
    const datesForTraits = {};
    featuresByDate.forEach(({ trait_list, date }) => {
      trait_list.forEach((technicalName) => {
        if (!datesForTraits[technicalName])
          datesForTraits[technicalName] = new Set();

        datesForTraits[technicalName].add(date);
      });
    });

    const traits = [];
    Object.entries(datesForTraits).forEach(([technicalName, dates]) => {
      /* Original trait is found by technicalName without classes however
       * we want to use the full name including classes later */
      const baseName = technicalName.split("|")[0];
      const trait = allTraits[baseName];
      traits.push({
        ...trait,
        technicalName,
        dates,
      });
    });

    /* Get imported traits using mergedTraits and filtering only traits from OTHER_NUMERICAL_PROPERTIES_GROUP
     * By doing this we will push the imported traits to the end of the traitsWithDates with `null` as the only available date */
    const importedTraits = mergedTraits.filter(
      ({ traitGroup }) => traitGroup.name === OTHER_NUMERICAL_PROPERTIES_GROUP
    );
    // Push imported traits deduced from features at current selected date
    importedTraits.forEach((trait) => {
      traits.push({
        ...trait,
        dates: new Set([null]),
      });
    });

    return traits;
  }, [featuresByDate, mergedTraits, allTraits]);

  const selectedDatesForTraitsDefaultValue = traitsWithDates.reduce(
    (traitsDates, trait) => ({
      ...traitsDates,
      /* Deselect heterogeneities, custom data and legacy traits by default */
      [trait.technicalName]:
        trait.technicalName.includes("heterogeneity") ||
        ["Legacy", OTHER_NUMERICAL_PROPERTIES_GROUP].includes(
          trait.traitGroup.name
        )
          ? new Set()
          : trait.dates,
    }),
    {}
  );

  const [selectedDatesForTraits, setSelectedDatesForTraits] = useState(
    selectedDatesForTraitsDefaultValue
  );

  /* Reset selectedDatesForTraits when featuresByDate changes because if some traits doesn't exist anymore
   * by selecting useFilteredData for example, it might break pca computation */
  const prevFeaturesbydate = useRef(featuresByDate);
  if (featuresByDate !== prevFeaturesbydate.current) {
    prevFeaturesbydate.current = featuresByDate;
    setSelectedDatesForTraits(selectedDatesForTraitsDefaultValue);
  }

  const [pca, setPca] = useState(null);

  // useEffect for autocompute
  useEffect(() => {
    if (options.autoCompute)
      setPca(
        performPCA(featuresByDate, selectedDatesForTraits, options.fillMethod)
      );
  }, [
    featuresByDate,
    options.autoCompute,
    options.fillMethod,
    selectedDatesForTraits,
  ]);

  const computedPCA = useMemo(() => {
    if (pca)
      return {
        ...pca,
        components: pca.components.filter(
          ({ variance }) => variance > options.threshold
        ),
      };
    else return null;
  }, [pca, options.threshold]);

  const [isOffcanvasOpen, setIsOffcanvasOpen] = useState(false);

  // Right rail tabs: analysis (correlation + variance) by default, auto-switch
  // to images on the first plot selection so comparison is never hidden when it
  // matters, while freeing the space the rest of the time.
  const [activeTab, setActiveTab] = useState("analysis");
  const prevClickedCount = useRef(0);
  useEffect(() => {
    if (prevClickedCount.current === 0 && clickedPlots.length > 0)
      setActiveTab("plots");
    prevClickedCount.current = clickedPlots.length;
  }, [clickedPlots.length]);

  // Parameters live in a left drawer opened from the header menu button; it
  // overlays the workspace so center + right keep full width when it's closed.
  // Its state comes from AnalyticsLayout: see drawerOpen there.
  const [inputOpen, setInputOpen] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(true);
  const [colorsOpen, setColorsOpen] = useState(false);
  // Mounted on the first opening, then kept while folded, so the Collapse
  // folds its content rather than an empty box (same as FieldBookCard).
  const [hasOpenedColors, setHasOpenedColors] = useState(false);
  const trial = useSelector(({ resultMap }) => resultMap.trial);
  useKeyboardShortcut("p", () => setDrawerOpen((open) => !open));

  // If pca computes with less components than previously selected, reset
  const correctedSelectedComponents =
    computedPCA &&
    selectedComponents.some((index) => index >= computedPCA.components.length)
      ? [0, 1]
      : selectedComponents;

  const { trackEvent } = useTracking();
  const toggleOffcanvas = () => {
    setIsOffcanvasOpen(!isOffcanvasOpen);
    trackEvent(ANALYTICS_EVENTS.ANALYTICS_AF_BUTTON);
  };

  const deselectPlot = (plot) => () => {
    setClickedPlots(clickedPlots.filter((p) => p.id !== plot.id));
  };

  const clearAllPlots = () => setClickedPlots([]);

  const componentsOptions = computedPCA
    ? computedPCA.components.map(({ name }, index) => ({
        label: name,
        value: index,
      }))
    : [];

  // Synchronize history scrolls
  useEffect(() => {
    const divs = document.querySelectorAll(".image-history");
    const syncScrolls = (div) => {
      divs.forEach((d) => {
        d.scrollTop = div.scrollTop;
        d.scrollLeft = div.scrollLeft;
      });
    };
    const eventListeners = [];
    divs.forEach((div) => {
      const eventListener = () => syncScrolls(div);
      eventListeners.push(eventListener);
      div.addEventListener("scroll", eventListener);
    });
    return () => {
      divs.forEach((div, index) =>
        div.removeEventListener("scroll", eventListeners[index])
      );
    };
    // Resync scrolls after adding/removing plots, and once the images tab opens
    // (its image-history nodes only mount then)
  }, [clickedPlots, activeTab]);

  const aggregatedFeatures = useSelector(selectFilteredAggregatedFeatures);
  const groups = useMemo(
    () => aggregatedFeatures.map(({ group }) => group),
    [aggregatedFeatures]
  );

  return (
    <div className="pca-page">
      <div className="pca-params-handle">
        <div
          className={`tab p-3 powerdash-component d-flex flex-row align-items-center clickable gap-2 ${
            drawerOpen ? "active" : ""
          }`}
          onClick={() => setDrawerOpen((open) => !open)}
        >
          <i className="fa-solid fa-sliders" />
          <span className="tab-text">PCA input &amp; settings</span>
          <kbd>P</kbd>
        </div>
      </div>
      <div className="pca-body">
        {/* LEFT — parameters drawer, overlays the workspace on demand */}
        {drawerOpen && (
          <div
            className="pca-drawer-backdrop"
            onClick={() => setDrawerOpen(false)}
          />
        )}
        <div className={`pca-drawer ${drawerOpen ? "open" : ""}`}>
          <div className="pca-drawer-header">
            <span className="pca-drawer-title">
              <i className="fa-solid fa-sliders" /> Input &amp; settings
            </span>
          </div>
          <ControlSection alwaysExpanded />
          <div className="pca-acc">
            <button
              type="button"
              className="pca-acc-head"
              onClick={() => {
                // Folds the colour list: see its head below.
                if (!inputOpen) setColorsOpen(false);
                setInputOpen((open) => !open);
              }}
            >
              <i className="fa-solid fa-right-to-bracket" /> PCA Input
              <i
                className={`fa-solid ms-auto ${
                  inputOpen ? "fa-chevron-up" : "fa-chevron-down"
                }`}
              />
            </button>
            <Collapse isOpen={inputOpen} className="pca-acc-body">
              <TraitsDatesMatrix
                key={traitsWithDates}
                traitsWithDates={traitsWithDates}
                selectedDatesForTraits={selectedDatesForTraits}
                setSelectedDatesForTraits={setSelectedDatesForTraits}
              />
            </Collapse>
          </div>
          <div className="pca-acc">
            <button
              type="button"
              className="pca-acc-head"
              onClick={() => {
                // Folds the colour list: see its head below.
                if (!settingsOpen) setColorsOpen(false);
                setSettingsOpen((open) => !open);
              }}
            >
              <i className="fa-solid fa-gear" /> PCA Settings
              <i
                className={`fa-solid ms-auto ${
                  settingsOpen ? "fa-chevron-up" : "fa-chevron-down"
                }`}
              />
            </button>
            <Collapse isOpen={settingsOpen} className="pca-acc-body">
              <PCASettings
                options={options}
                setOptions={setOptions}
                handleCompute={() =>
                  setPca(
                    performPCA(
                      featuresByDate,
                      selectedDatesForTraits,
                      options.fillMethod
                    )
                  )
                }
                disableCompute={featuresByDate.length === 0}
              />
            </Collapse>
          </div>
          {trial && (
            <div className="pca-acc">
              <button
                type="button"
                className="pca-acc-head"
                onClick={() => {
                  // The drawer clips instead of scrolling: the colour list
                  // would push its Apply bar out of it under the matrix and
                  // the settings, so it opens alone.
                  if (!colorsOpen) {
                    setInputOpen(false);
                    setSettingsOpen(false);
                    setHasOpenedColors(true);
                  }
                  setColorsOpen(!colorsOpen);
                }}
              >
                <i className="fa-solid fa-palette" /> Fieldbook colors
                <i
                  className={`fa-solid ms-auto ${
                    colorsOpen ? "fa-chevron-up" : "fa-chevron-down"
                  }`}
                />
              </button>
              <Collapse isOpen={colorsOpen} className="pca-acc-body">
                {hasOpenedColors && <TrialPreferences />}
              </Collapse>
            </div>
          )}
        </div>
        {/* Close handle on the drawer's right edge — mirror of the advanced-
            filtering tab, pointing left to hide the drawer. */}
        {drawerOpen && (
          <div
            className="pca-drawer-close-tab p-3 d-flex align-items-center clickable"
            title="Hide parameters"
            onClick={() => setDrawerOpen(false)}
          >
            <i className="fa-solid fa-angles-left" />
          </div>
        )}

        {/* CENTER — title + plots projection + search (always visible) */}
        <div className="pca-center">
          {computedPCA && (
            <div className="pca-title">
              <span className="pca-title-line">
                <i className="fa-solid fa-circle-nodes discrete-icon" />
                <span className="pca-title-label">
                  Principal component analysis
                </span>
                <a
                  href="https://en.wikipedia.org/wiki/Principal_component_analysis"
                  target="_blank"
                  rel="noreferrer"
                >
                  <i className="fa-solid fa-arrow-up-right-from-square discrete-icon" />
                </a>
              </span>
              <span className="pca-title-line discrete-text">
                <Badge className="hiphen-badge active small">
                  {computedPCA.components.length} Principal components
                </Badge>{" "}
                extracted, reducing{" "}
                <span className="font-weight-bold">
                  {computedPCA.columns.length}
                </span>{" "}
                selected dimensions
              </span>
            </div>
          )}
          <div className="pca-scatter-card">
            <Chart
              icon="fa-solid fa-arrows-up-down-left-right"
              title={PCA_SCATTER_TITLE}
              isLoading={refreshingFeaturesByDate}
              missingData={!computedPCA || computedPCA.components.length < 2}
              topRightContent={
                <>
                  {computedPCA && (
                    <DisplayedComponents
                      id="pca-comp-scatter"
                      options={componentsOptions}
                      selected={selectedComponents}
                      setSelected={setSelectedComponents}
                    />
                  )}
                  <HelpTooltip helpTooltip={HELP_TOOLTIPS.PCA_SCATTER} />
                </>
              }
            >
              <PCAScatter
                selectedComponents={correctedSelectedComponents}
                computedPCA={computedPCA}
                clickedPlots={clickedPlots}
                setClickedPlots={setClickedPlots}
              />
            </Chart>
          </div>
          <div className="pca-search-card">
            <FocusList groups={groups} focusPlots />
          </div>
        </div>

        {/* RIGHT — tabbed rail: analysis / images */}
        <div className="pca-right-rail">
          <Nav tabs className="analytics-tabs">
            <NavItem>
              <NavLink
                className={activeTab === "analysis" ? "active" : ""}
                onClick={() => setActiveTab("analysis")}
              >
                <i className="fa-solid fa-compass" /> Analysis
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={activeTab === "plots" ? "active" : ""}
                onClick={() => setActiveTab("plots")}
              >
                <i className="fa-solid fa-scale-balanced" /> Plots
                {clickedPlots.length > 0 && (
                  <span className="pca-tab-badge">{clickedPlots.length}</span>
                )}
              </NavLink>
            </NavItem>
            {clickedPlots.length > 0 && (
              <button
                type="button"
                className="pca-clear-all-btn"
                onClick={clearAllPlots}
              >
                <i className="fa-solid fa-trash-can" /> Remove all plots
              </button>
            )}
          </Nav>
          <TabContent activeTab={activeTab} className="pca-tab-content">
            <TabPane tabId="analysis" className="pca-analysis-pane">
              <div className="pca-correlation">
                <Chart
                  icon="fa-solid fa-compass"
                  title={CIRCLE_CHART_TITLE}
                  topRightContent={
                    <>
                      {computedPCA && (
                        <DisplayedComponents
                          id="pca-comp-circle"
                          options={componentsOptions}
                          selected={selectedComponents}
                          setSelected={setSelectedComponents}
                        />
                      )}
                      <HelpTooltip helpTooltip={HELP_TOOLTIPS.PCA_CIRCLE} />
                    </>
                  }
                  isLoading={refreshingFeaturesByDate}
                  missingData={
                    !computedPCA || computedPCA.components.length < 2
                  }
                >
                  <CircleChart
                    computedPCA={computedPCA}
                    selectedComponents={correctedSelectedComponents}
                  />
                </Chart>
              </div>
              <div className="pca-variance">
                <Chart
                  icon="fa-solid fa-chart-bar"
                  title={BAR_CHART_TITLE}
                  topRightContent={
                    computedPCA && (
                      <DisplayedComponents
                        id="pca-comp-variance"
                        options={componentsOptions}
                        selected={selectedComponents}
                        setSelected={setSelectedComponents}
                      />
                    )
                  }
                  isLoading={refreshingFeaturesByDate}
                  missingData={
                    !computedPCA || computedPCA.components.length < 1
                  }
                >
                  <BarChart
                    computedPCA={computedPCA}
                    selectedComponents={correctedSelectedComponents}
                  />
                </Chart>
              </div>
            </TabPane>
            <TabPane tabId="plots" className="pca-images-pane">
              {clickedPlots.length === 0 ? (
                <div className="pca-images-empty">
                  <i className="fa-solid fa-hand-pointer" />
                  <span>
                    Click a point on the projection to compare its plots
                  </span>
                </div>
              ) : (
                <div className="pca-images-grid">
                  {clickedPlots.map((plot) => (
                    <div className="pca-images-card" key={plot.id}>
                      <PlotHistory
                        plot={plot}
                        deselectPlot={deselectPlot(plot)}
                      />
                    </div>
                  ))}
                </div>
              )}
            </TabPane>
          </TabContent>
        </div>
      </div>
      <OffCanvasWrapper
        isOffcanvasOpen={isOffcanvasOpen}
        toggleOffcanvas={toggleOffcanvas}
      />
    </div>
  );
};

PrincipalComponentAnalysis.propTypes = {
  drawerOpen: PropTypes.bool.isRequired,
  setDrawerOpen: PropTypes.func.isRequired,
};
